'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react'
import { FlowEditor } from '@/components/pbx/flow-editor'
import { useCallFlow, useCallFlowMutations } from '@/hooks/pbx'
import type { CallFlow } from '@/lib/pbx/flow/types'
import { useTranslations } from "next-intl"


interface FlowEditorViewProps {
  flowId: string | null
  isNew: boolean
  onClose: () => void
}

export function FlowEditorView({ flowId, isNew, onClose }: FlowEditorViewProps) {
  const tUi = useTranslations("Pbx.PbxPagesFlowEditorView")
  const tCommonUi = useTranslations("Common")

  const { flow, isLoading, error } = useCallFlow(flowId)
  const { createFlow, isLoading: isCreating } = useCallFlowMutations()

  const [newFlowDialogOpen, setNewFlowDialogOpen] = useState(isNew)
  const [newFlowName, setNewFlowName] = useState('')
  const [newFlowDescription, setNewFlowDescription] = useState('')
  const [createdFlow, setCreatedFlow] = useState<CallFlow | null>(null)

  // Handle creating a new flow
  const handleCreateFlow = async () => {
    if (!newFlowName.trim()) return

    try {
      // Don't pass nodes/edges - server creates default flow with incoming_call → hangup
      const created = await createFlow({
        name: newFlowName.trim(),
        description: newFlowDescription.trim() || undefined,
      })
      setCreatedFlow(created)
      setNewFlowDialogOpen(false)
    } catch {
      // Error is handled by the hook
    }
  }

  // If we're editing an existing flow, wait for it to load
  if (flowId && !isNew) {
    if (isLoading) {
      return (
        <div className="flex min-h-[45vh] items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto w-8 h-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground mt-2"><span key={tUi("loadingFlow")}>{tUi("loadingFlow")}</span></p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="p-6 max-w-xl mx-auto">
          <Button variant="ghost" onClick={onClose} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span><span key={tUi("back")}>{tUi("back")}</span></span>
          </Button>
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">{error}</AlertDescription>
          </Alert>
        </div>
      )
    }

    if (!flow) {
      return (
        <div className="p-6 max-w-xl mx-auto">
          <Button variant="ghost" onClick={onClose} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span><span key={tUi("back")}>{tUi("back")}</span></span>
          </Button>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription><span key={tUi("flowNotFound")}>{tUi("flowNotFound")}</span></AlertDescription>
          </Alert>
        </div>
      )
    }

    return (
      <div className="h-full flex flex-col">
        <div className="border-b bg-card p-2 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span><span key={tUi("back")}>{tUi("back")}</span></span>
          </Button>
        </div>
        <div className="flex-1 overflow-hidden">
          <FlowEditor flow={flow} />
        </div>
      </div>
    )
  }

  // For new flows, show the creation dialog first
  if (isNew && !createdFlow) {
    return (
      <>
        <div className="p-6 max-w-xl mx-auto">
          <Button variant="ghost" onClick={onClose} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span><span key={tUi("back")}>{tUi("back")}</span></span>
          </Button>
          <div className="text-center py-12">
            <p className="text-muted-foreground"><span key={tUi("creatingNewFlow")}>{tUi("creatingNewFlow")}</span></p>
          </div>
        </div>

        <Dialog open={newFlowDialogOpen} onOpenChange={(open) => {
          if (!open) onClose()
          setNewFlowDialogOpen(open)
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle><span key={tUi("createNewFlow")}>{tUi("createNewFlow")}</span></DialogTitle>
              <DialogDescription>
                <span key={tUi("giveYourFlowANameToGetStarted")}>{tUi("giveYourFlowANameToGetStarted")}</span></DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="flow-name"><span key={tUi("flowName")}>{tUi("flowName")}</span></Label>
                <Input
                  id="flow-name"
                  value={newFlowName}
                  onChange={(e) => setNewFlowName(e.target.value)}
                  placeholder={tUi("mainIVRFlow")}
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="flow-description"><span key={tUi("descriptionOptional")}>{tUi("descriptionOptional")}</span></Label>
                <Textarea
                  id="flow-description"
                  value={newFlowDescription}
                  onChange={(e) => setNewFlowDescription(e.target.value)}
                  placeholder={tUi("handlesIncomingCallsDuringBusinessHours")}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button
                onClick={handleCreateFlow}
                disabled={isCreating || !newFlowName.trim()}
              >
                {isCreating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                <span><span key={tUi("create")}>{tUi("create")}</span></span>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  // Show the editor for the newly created flow
  if (createdFlow) {
    return (
      <div className="h-full flex flex-col">
        <div className="border-b bg-card p-2 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span><span key={tUi("back")}>{tUi("back")}</span></span>
          </Button>
        </div>
        <div className="flex-1 overflow-hidden">
          <FlowEditor flow={createdFlow} />
        </div>
      </div>
    )
  }

  return null
}
